import { useEffect, useRef } from 'react';
import StatementUpload from '../assessment/StatementUpload.jsx';
import ExtractionReview from '../assessment/ExtractionReview.jsx';

// The one thing a planning admin does for an assessment: put the statements
// in. Eugene's direction on 2026-09-29 was to strip the preparation screen to
// exactly this, "just an upload of the statements", because every other
// figure now lives in the questionnaire and is corrected in the meeting.
//
// Reading statements takes about a minute, and a client-facing conversation
// must not wait on it, so this still happens before the meeting: upload,
// review, accept, and the portfolio picture is there when the advisor opens
// the assessment.

const WORDS = {
  absent: 'No statements supplied.',
  pending: 'Reading the statements.',
  failed: 'The read did not complete. Nothing from it is shown.',
  unreviewed: 'Results are waiting for review.',
  ready: 'Reviewed. The portfolio picture is available in the meeting.',
};

export default function StatementsDialog({
  open, onClose, analysis, bundle, uploadStatus, accepted, onAccept, onUnaccept, onBundle, onStatus, name, readOnly, demo,
}) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  const state = analysis?.availability?.state || 'absent';

  return (
    <dialog className="conv-statements" ref={ref} aria-label="Statements and portfolio analysis" onCancel={e => { e.preventDefault(); onClose(); }}>
      <div className="conv-board-top">
        <div>
          <span className="conv-eyebrow">Statements</span>
          <h2>Portfolio analysis{name ? ` for ${name}` : ''}</h2>
        </div>
        <button type="button" className="conv-btn" onClick={onClose}>Close</button>
      </div>
      <p className="conv-quiet">
        Optional. Upload the client's statements here before the meeting; the analysis runs and the portfolio picture is ready when the meeting opens.
      </p>

      <div className="conv-analysis-state" data-state={state}>
        <strong>{WORDS[state]}</strong>
        {analysis?.availability?.reason ? <span>{analysis.availability.reason}</span> : null}
      </div>

      {demo ? (
        <div className="conv-demo-statements">
          <p className="conv-source">Simulated statements only. These controls do not upload files or call an analysis service.</p>
          <div className="conv-actions">
            <button className="conv-btn" disabled={readOnly} type="button" onClick={async () => {
              if (import.meta.env.VITE_DEMO !== 'true') return;
              const { demoHouseholdData } = await import('../../store/demoHouseholds.js');
              onBundle(demoHouseholdData('quinn-avery').assessment.holdings);
              onStatus({ status: 'done', message: 'Sample results ready for review.' });
            }}>Use sample statements</button>
            {bundle && <button className="conv-btn" disabled={readOnly} type="button" onClick={() => { onBundle(null); onStatus({ status: 'idle' }); }}>Clear sample statements</button>}
          </div>
        </div>
      ) : !readOnly && (
        <StatementUpload
          analysis={uploadStatus}
          onStatus={onStatus}
          onBundle={onBundle}
          clientName={name}
        />
      )}

      {bundle ? <ExtractionReview bundle={bundle} /> : null}

      {bundle && (
        <div className="conv-actions" style={{ marginTop: 14 }}>
          {/* Taking them out again, whenever they went in (Jimmy, 2026-10-16).
              The analysis is worked out across the whole batch, so they come
              out together and the ones wanted go back in. */}
          {!demo && !readOnly ? (
            <button
              type="button"
              className="conv-btn"
              onClick={() => {
                if (!window.confirm('Remove these statements from the analysis? Nothing from them will be shown until statements are added again.')) return;
                onBundle(null);
                onStatus({ status: 'idle' });
              }}
            >
              Remove the statements
            </button>
          ) : null}
          {accepted ? (
            <button type="button" className="conv-btn" onClick={onUnaccept} disabled={readOnly}>
              Withdraw the review
            </button>
          ) : (
            <button type="button" className="conv-btn is-primary" onClick={onAccept} disabled={readOnly}>
              Accept these results
            </button>
          )}
          <span className="conv-quiet">
            {accepted
              ? 'Replacing the statements withdraws this automatically.'
              : 'Nothing from the statements is shown until this is accepted.'}
          </span>
        </div>
      )}
    </dialog>
  );
}
